/**
 * CitationChip - the one citation affordance shared by every typed renderer.
 *
 * Clicking a chip opens the EXISTING document viewer / citation panel:
 *   • `onOpenCitationRequest` when the host supports the richer contract
 *     (carries the citation and, when the engine gave chunker-grade offsets,
 *     the character range to highlight);
 *   • `onOpenCitation(url)` otherwise - the legacy string callback.
 *
 * Offsets from `snippet_match` / `unavailable` are deliberately dropped:
 * a highlight we cannot vouch for is worse than no highlight.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { PiPaperclipLight } from 'react-icons/pi';
import { sectionLocator } from '../citationMarkers';
import {
  CitationOpenRequest,
  CitationSection,
  ResearchCitation,
  citationHighlight,
  isUploadedDocument,
} from '../types';

export interface CitationHandlers {
  /** Legacy string callback - always available. */
  onOpenCitation: (url: string) => void;
  /** Richer callback that can carry the highlight range. */
  onOpenCitationRequest?: (request: CitationOpenRequest) => void;
}

/** Build the open request for a citation, applying the highlight rules. */
export function citationOpenRequest(citation: ResearchCitation): CitationOpenRequest {
  return {
    citation,
    target: citation.url || citation.title || `#citation-${citation.number}`,
    highlight: citationHighlight(citation),
  };
}

/** Fire whichever citation-open contract the host provided. */
export function openCitation(
  citation: ResearchCitation,
  handlers: CitationHandlers,
  /** Aim at one matching passage instead of the citation's primary one. */
  section?: CitationSection
): void {
  const request = citationOpenRequest(
    section
      ? { ...citation, locator: sectionLocator(section) }
      : citation
  );
  if (handlers.onOpenCitationRequest) {
    handlers.onOpenCitationRequest(request);
    return;
  }
  handlers.onOpenCitation(request.target);
}

/** Look a citation number up in the envelope's citation list. */
export function findCitation(
  citations: ResearchCitation[],
  number: number
): ResearchCitation | undefined {
  return citations.find((c) => c.number === number);
}

/** Both spellings the engine has used for citation references on a payload node. */
export function citationNumbers(node?: {
  citations?: number[];
  citation_numbers?: number[];
}): number[] {
  const raw = node?.citations ?? node?.citation_numbers ?? [];
  if (!Array.isArray(raw)) return [];
  return raw.map((n) => Number(n)).filter((n) => Number.isFinite(n));
}

interface CitationChipProps extends CitationHandlers {
  citation: ResearchCitation;
}

/** A single `[N]` chip. */
export const CitationChip: React.FC<CitationChipProps> = ({ citation, ...handlers }) => {
  const { t } = useTranslation();
  const uploaded = isUploadedDocument(citation);
  const highlight = citationHighlight(citation);
  const label = citation.title || citation.file_name || `${t('Citation')} ${citation.number}`;

  return (
    <button
      type="button"
      onClick={() => openCitation(citation, handlers)}
      title={highlight ? `${label} - ${t('exact passage')}` : label}
      aria-label={label}
      data-citation-number={citation.number}
      data-highlight={highlight ? 'true' : 'false'}
      className="inline-flex items-center gap-0.5 align-super mx-0.5 px-1.5 py-px rounded-full
                 text-[10px] font-semibold leading-none
                 bg-surface-sunken text-fg-muted border border-hairline-soft
                 hover:text-champagne hover:border-champagne/50 transition-colors duration-200 ease-slide"
    >
      {uploaded && <PiPaperclipLight className="w-2.5 h-2.5" aria-hidden />}
      {citation.number}
    </button>
  );
};

interface CitationChipsProps extends CitationHandlers {
  numbers: number[];
  citations: ResearchCitation[];
}

/** The chips for one payload node, skipping numbers with no matching citation. */
export const CitationChips: React.FC<CitationChipsProps> = ({
  numbers,
  citations,
  ...handlers
}) => {
  if (!numbers.length) return null;
  return (
    <>
      {numbers.map((n) => {
        const citation = findCitation(citations, n);
        if (!citation) return null;
        return <CitationChip key={n} citation={citation} {...handlers} />;
      })}
    </>
  );
};

/** The "your file" affordance shown next to uploaded-document sources. */
export const UploadedFileBadge: React.FC<{ fileName?: string }> = ({ fileName }) => {
  const { t } = useTranslation();
  return (
    <span
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded
                 text-[10px] font-medium text-fg-muted bg-surface-sunken border border-hairline-soft"
      title={fileName}
    >
      <PiPaperclipLight className="w-3 h-3" aria-hidden />
      {t('your file')}
    </span>
  );
};

export default CitationChip;
